import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, apiGet } from "../api";
import { toast } from "sonner";
import { InvoiceUpload } from "../components/InvoiceUpload";
import { InvoiceFilterDelete } from "../components/InvoiceFilterDelete";
import usePageTitle from "../hooks/usePageTitle";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Card } from "../components/dc";
import { formatCurrency, formatNumber } from "../lib/format";

const PAGE_SIZE = 100;
// Server-side paginated (624k+ rows) — search, sort + Load More hit the API, so
// this keeps its own paging rather than the client-side DataTable. Styled to the
// reference comp. Endpoints preserved: /invoices, /missing-invoices, DELETE /invoices.
const COLS = "0.9fr 0.7fr 0.9fr 1.6fr 0.8fr 0.7fr 0.5fr 0.8fr";

export default function Invoices() {
  usePageTitle("Invoices");
  const [invoices, setInvoices] = useState([]);
  const [missingInvoices, setMissingInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");

  const loadInvoices = useCallback(async (skipVal = 0, search = "", sf = "invoice_date", sd = "desc") => {
    const isInitial = skipVal === 0;
    if (isInitial) setInitialLoading(true); else setLoadingMore(true);
    try {
      let url = `/invoices?limit=${PAGE_SIZE}&skip=${skipVal}&sort_field=${encodeURIComponent(sf)}&sort_direction=${encodeURIComponent(sd)}`;
      if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
      const data = await apiGet(url, { timeout: 20000 });
      if (data) {
        const list = Array.isArray(data.invoices) ? data.invoices : [];
        setInvoices((prev) => (isInitial ? list : [...prev, ...list]));
        setTotalCount(typeof data.total === "number" ? data.total : 0);
        setSkip(skipVal + list.length);
      }
    } finally {
      setInitialLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);
  useEffect(() => { loadInvoices(0, debouncedSearch, sortField, sortDir); }, [debouncedSearch, sortField, sortDir, loadInvoices]);

  const loadMissingInvoices = useCallback(async () => {
    try {
      const data = await apiGet("/missing-invoices", { timeout: 60000 });
      setMissingInvoices(Array.isArray(data) ? data : []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadMissingInvoices(); }, [loadMissingInvoices]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API}/invoices`);
      toast.success("All invoices deleted");
      setInvoices([]); setTotalCount(0); setSkip(0); setShowDeleteDialog(false);
    } catch { toast.error("Failed to delete invoices"); }
  };

  const reload = () => loadInvoices(0, debouncedSearch, sortField, sortDir);
  const hasMore = invoices.length < totalCount;

  // Clickable header cell — server-side sort with ↑/↓/⇅ indicator.
  const SH = ({ field, label, align }) => (
    <button data-testid={`invoice-sort-${field}`} type="button" onClick={() => handleSort(field)} style={{ border: 0, background: "none", color: "inherit", font: "inherit", cursor: "pointer", userSelect: "none", textAlign: align || "left" }}>
      {label} {sortField === field ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
    </button>
  );

  return (
    <div data-testid="invoices-page">
      {missingInvoices.length > 0 && (
        <div style={{ background: "var(--color-bg-hemocyanin)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 14, padding: "18px 22px", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15.5, color: "#fbbf24", marginBottom: 4 }}>⚠ {missingInvoices.length} supplier/month gaps missing invoices</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {missingInvoices.map((m) => (
              <div key={`${m.supplier}-${m.start_month}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--color-text-3)" }}>
                <span style={{ color: "var(--color-text-2)" }}>{m.supplier} — {m.start_month === m.end_month ? m.start_month : `${m.start_month} to ${m.end_month}`}</span>
                <span data-testid={`missing-invoice-${m.supplier}`}>{m.missing_count} month{m.missing_count !== 1 ? "s" : ""}{m.price_file_present_count > 0 ? ` · ${m.price_file_present_count} with a price file` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload + targeted delete */}
      <div style={{ display: "grid", gridTemplateColumns: totalCount > 0 ? "1fr 1fr" : "1fr", gap: 18, marginBottom: 20, alignItems: "start" }}>
        <InvoiceUpload onSuccess={reload} />
        {totalCount > 0 && <InvoiceFilterDelete onDeleted={reload} />}
      </div>

      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700 }}>
            Invoices {totalCount > 0 && <span style={{ color: "#786a97", fontWeight: 500, fontSize: 14 }}>({formatNumber(totalCount)} total)</span>}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", minWidth: 0, alignItems: "center", flex: "1 1 200px", justifyContent: "flex-end" }}>
            {totalCount > 0 && (
              <span onClick={() => setShowDeleteDialog(true)} data-testid="delete-all-invoices-btn" style={{ color: "#fb7185", fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>Delete All</span>
            )}
            <input
              data-testid="invoice-search"
              aria-label="Search invoices"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search SKU, description, supplier, invoice ref..."
              style={{ flex: 1, maxWidth: 340, background: "var(--color-bg-panel)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: "10px 14px", color: "var(--color-text-1)", fontSize: 14, fontFamily: "var(--font-family)" }}
            />
          </div>
        </div>

        <div data-testid="invoice-table-scroll" role="region" aria-label="Invoice table" tabIndex={0} style={{ overflowX: "auto", maxWidth: "100%" }}><div style={{ minWidth: 900 }}>
        <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "0 4px 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-4)", borderBottom: "1px solid var(--hairline)" }}>
          <SH field="invoice_reference" label="Invoice Ref" />
          <SH field="invoice_date" label="Date" />
          <SH field="sku" label="Product Code" />
          <SH field="description" label="Description" />
          <SH field="supplier" label="Supplier" />
          <SH field="unit_price" label="Unit Price" />
          <SH field="quantity" label="Qty" />
          <SH field="total" label="Total" align="right" />
        </div>

        {initialLoading ? (
          <div style={{ padding: "28px 4px", textAlign: "center", color: "var(--color-text-3)" }}>Loading invoices…</div>
        ) : invoices.length === 0 ? (
          <div style={{ padding: "28px 4px", textAlign: "center", color: "var(--color-text-3)" }}>
            {debouncedSearch ? `No invoices found matching "${debouncedSearch}"` : "No invoices yet. Upload a file above."}
          </div>
        ) : (
          <>
            {invoices.map((inv) => (
              <div key={inv.id} style={{ display: "grid", gridTemplateColumns: COLS, alignItems: "center", padding: "13px 4px", borderBottom: "1px solid var(--hairline-soft)", fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: "#67e0f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.invoice_reference || "—"}</div>
                <div style={{ color: "var(--color-text-3)", fontVariantNumeric: "tabular-nums" }}>{inv.invoice_date}</div>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.sku}</div>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--color-text-2)" }} title={inv.description}>{inv.description}</div>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.supplier}</div>
                <div style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(inv.unit_price)}</div>
                <div style={{ fontVariantNumeric: "tabular-nums" }}>{inv.quantity}</div>
                <div style={{ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{formatCurrency(inv.total)}</div>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
              <div style={{ fontSize: 13, color: "var(--color-text-3)" }}>Showing {formatNumber(invoices.length)} of {formatNumber(totalCount)}</div>
              {hasMore && (
                <button type="button" disabled={loadingMore} onClick={() => loadInvoices(skip, debouncedSearch, sortField, sortDir)} data-testid="load-more-btn"
                  style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", fontSize: 13, fontWeight: 600, cursor: loadingMore ? "wait" : "pointer", color: "var(--color-text-2)" }}>
                  {loadingMore ? "Loading…" : `Load more (${formatNumber(totalCount - invoices.length)} remaining)`}
                </button>
              )}
            </div>
          </>
        )}
        </div></div>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Invoices?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {formatNumber(totalCount)} invoice items. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={handleDeleteAll}>Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
